import React from 'react';
import { useCurrentSettings } from '@hooks/useCurrentSettings';
import type { Routing } from '../../routes';

type AppProps = {
  router: Routing;
  routes: Routing;
};

type PropsAreEqual<P> = (
  prevProps: Readonly<P>,
  nextProps: Readonly<P>,
) => boolean;

const withFilteredRoutes = <P extends AppProps>(
  component: {
    (props: Omit<P, 'router'>): Exclude<React.ReactNode, undefined>;
    displayName?: string;
  },
  propsAreEqual?: PropsAreEqual<P> | false,

  componentName = component.displayName ?? component.name,
): {
  (props: Omit<P, 'routes'>): JSX.Element;
  displayName: string;
} => {
  function WithFeatureFlags(props: P) {
    const currentUser = useCurrentSettings();
    const routes = props.router.filter((route) => {
      const hasFF = currentUser?.featureSwitches?.includes(route?.featureFlag);

      if (!route?.featureFlag || hasFF) {
        return route;
      }
    });

    return component({ routes, ...props }) as JSX.Element;
  }

  WithFeatureFlags.displayName = `withFilteredRoutes(${componentName})`;

  let wrappedComponent =
    propsAreEqual === false
      ? WithFeatureFlags
      : React.memo(WithFeatureFlags, propsAreEqual);

  return wrappedComponent as typeof WithFeatureFlags;
};

export { withFilteredRoutes };
